import React, { useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
export function DoNotSellMyInfoPage() {
  usePageMeta('Do Not Sell My Info | OnlineCollegeGuide', 'Exercise your right to opt out of the sale or sharing of your personal information.');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    state: '',
    note: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Opt-out request submitted:', formData);
    // Send the request to the admin inbox via the visitor's email client.
    const subject = 'California Privacy Opt-Out Request';
    const body = ['A new Do Not Sell or Share My Personal Information request has been submitted:', '', `Full Name: ${formData.fullName}`, `Email: ${formData.email}`, `Phone: ${formData.phone || 'N/A'}`, `State of Residence: ${formData.state}`, `Additional Notes: ${formData.note || 'N/A'}`].join('\n');
    const mailtoUrl = `mailto:admin@media-matchers.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
    setIsSubmitted(true);
  };
  return <main className="bg-white py-10 sm:py-16" data-id="element-941">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-800" data-id="element-942">
        <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-navy mb-8 transition-colors" data-id="element-943">
          <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-944" />
          Back to home
        </Link>

        {/* Page header */}
        <header className="border-b border-gray-200 pb-8 mb-12" data-id="element-945">
          <h1 className="text-4xl sm:text-5xl font-bold text-navy tracking-tight leading-tight" data-id="element-946">
            Do Not Sell or Share My Personal Information
          </h1>
        </header>

        {/* Intro */}
        <div className="space-y-5 leading-relaxed text-base mb-12" data-id="element-947">
          <p data-id="element-948">
            Under the California Consumer Privacy Act (CCPA) and the California
            Privacy Rights Act (CPRA), California residents have the right to
            opt out of the "sale" or "sharing" of their personal information.
          </p>
          <p data-id="element-949">
            <strong data-id="element-950">What does "sale" or "sharing" mean?</strong> In the context
            of our services, we do not sell your personal information for money.
            However, we may share your information with educational partners to
            fulfill your requests for information about degree programs, or with
            advertising partners to deliver targeted advertisements. Under
            California law, these activities may be considered a "sale" or
            "sharing" of personal information.
          </p>
          <p data-id="element-951">
            If you would like to exercise your right to opt out of these
            activities, please complete the form below. Alternatively, you may
            email us at{' '}
            <a href="mailto:contact@onlinecollegeguide.io" className="text-navy underline hover:text-crimson" data-id="element-952">
              contact@onlinecollegeguide.io
            </a>{' '}
            with the subject line "California Privacy Opt-Out".
          </p>
          <p data-id="element-953">
            For more details on how we handle your data, please review our{' '}
            <Link to="/privacy-policy" className="text-navy underline hover:text-crimson" data-id="element-954">
              Privacy Policy
            </Link>
            .
          </p>
        </div>

        {/* Form Section */}
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 sm:p-10" data-id="element-955">
          {isSubmitted ? <div className="text-center py-8" data-id="element-956">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6" data-id="element-957">
                <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-958" />
              </div>
              <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-959">
                Request Received
              </h2>
              <p className="text-gray-600 max-w-md mx-auto" data-id="element-960">
                Thank you. We have received your request to opt out of the sale
                or sharing of your personal information. We will process your
                request within 15 business days.
              </p>
            </div> : <>
              <h2 className="text-2xl font-bold text-navy mb-6" data-id="element-961">
                Opt-Out Request Form
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-962">
                <div data-id="element-963">
                  <label htmlFor="fullName" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-964">
                    Full Name <span className="text-crimson" data-id="element-965">*</span>
                  </label>
                  <input type="text" id="fullName" required value={formData.fullName} onChange={e => setFormData({
                ...formData,
                fullName: e.target.value
              })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy" data-id="element-966" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-967">
                  <div data-id="element-968">
                    <label htmlFor="email" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-969">
                      Email Address <span className="text-crimson" data-id="element-970">*</span>
                    </label>
                    <input type="email" id="email" required value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy" data-id="element-971" />
                  </div>
                  <div data-id="element-972">
                    <label htmlFor="phone" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-973">
                      Phone Number{' '}
                      <span className="text-gray-500 font-normal" data-id="element-974">
                        (Optional)
                      </span>
                    </label>
                    <input type="tel" id="phone" value={formData.phone} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy" data-id="element-975" />
                  </div>
                </div>

                <div data-id="element-976">
                  <label htmlFor="state" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-977">
                    State of Residence <span className="text-crimson" data-id="element-978">*</span>
                  </label>
                  <select id="state" required value={formData.state} onChange={e => setFormData({
                ...formData,
                state: e.target.value
              })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-navy" data-id="element-979">
                    <option value="" data-id="element-980">Select a state</option>
                    <option value="CA" data-id="element-981">California</option>
                    <option value="Other" data-id="element-982">Other</option>
                  </select>
                </div>

                <div data-id="element-983">
                  <label htmlFor="note" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-984">
                    Additional Notes{' '}
                    <span className="text-gray-500 font-normal" data-id="element-985">
                      (Optional)
                    </span>
                  </label>
                  <textarea id="note" rows={4} value={formData.note} onChange={e => setFormData({
                ...formData,
                note: e.target.value
              })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy resize-none" placeholder="Please provide any additional details regarding your request..." data-id="element-986" />
                </div>

                <button type="submit" className="w-full bg-crimson hover:bg-[#8B1A1A] text-white font-bold py-4 px-6 rounded-lg text-base transition-colors duration-200 shadow-sm hover:shadow-md" data-id="element-987">
                  Submit Opt-Out Request
                </button>
              </form>
            </>}
        </div>
      </div>
    </main>;
}